import {GamesMap, IGameImageSize, IGamesService, RecentlyPlayedGames} from './games.service.interface';
import {GameInfo} from './game-info';
import {action, observable, makeObservable, computed} from 'mobx';
import {ServiceBase} from '../ServiceBase';
import {Check} from '@sparkware/app-utils';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {SAVED_GAMES_LOCAL_STORAGE_KEY} from "../../../common/const/saved-games-ls-key_const";
import {SwiperGamesListConfiguration} from "../configurations/config-data-sections/arenas.configuration";
import {RecentlyPlayedGamesReader} from "./recently-played-games-reader";
import {CorrelatedGamesReader} from "./correlated-games-reader";

export class GamesService extends ServiceBase implements IGamesService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);
    this._recentlyPlayedGamesReader = new RecentlyPlayedGamesReader(this.services);
    this._correlatedGamesReader = new CorrelatedGamesReader(this.services);

    this.reaction(
      () => this.services.user.isAuthorized,
      () => {
        this._updateGamesMapPromise = this._updateGamesMap()
      },
      {
        fireImmediately: true
      });

    this._initializeSavedGames();
  }

  private readonly _recentlyPlayedGamesReader: RecentlyPlayedGamesReader;
  private readonly _correlatedGamesReader: CorrelatedGamesReader;

  //This is an in-memory copy of the games saved in local storage. We need it so that components can react to changes in the array of saved games.
  @observable.ref
  savedGameTypes: number[] = [];

  @action.bound
  private _setInMemorySavedGames(newSavedGames: number[]): void {
    this.savedGameTypes = newSavedGames
  }

  private _initializeSavedGames(): void {
    let localStorageSavedGames = this.services.localStorage.getJson<number[]>(SAVED_GAMES_LOCAL_STORAGE_KEY);
    this._setInMemorySavedGames(Check.isNullOrUndefined(localStorageSavedGames) ? [] : localStorageSavedGames);
  }

  @observable.ref
  private _gamesMap: GamesMap = {};

  @computed
  get gamesMap(): GamesMap {
      const result: GamesMap = {};
      for(let gameInfo of Object.values(this._gamesMap)) {
        if(gameInfo.restriction.isGameAllowedInTheGrid()) {
          result[gameInfo.gameType] = gameInfo;
        }
      }
      return result;
  }


  @action.bound
  private _setGamesMap(gamesMap: GamesMap): void {
    this._gamesMap = gamesMap;
  }


  get recentlyPlayedGames(): RecentlyPlayedGames {
      return this._recentlyPlayedGamesReader.recentlyPlayedGames;
  };

  async getRecentlyPlayedGamesAsync(): Promise<number[]> {
    return await this._recentlyPlayedGamesReader.getRecentlyPlayedGamesAsync();
  }


  private async _buildGamesMap(sdkGamesInfo: any): Promise<GamesMap> {


    let gamesMap: GamesMap = {};

    const usedGameTypesBySiteMap = this.services.configuration.usedGameTypesBySite.reduce((accumulator, gt) => {
        accumulator[gt] = true;
        return accumulator;
    }, {});

    Object.keys(sdkGamesInfo).forEach(gameType => {
      try {
        // filter only in-layout games
        if (usedGameTypesBySiteMap[gameType]) {
            let sdkInfo = sdkGamesInfo[gameType];
            let generalSettings = this.services.configuration.data.generalSettings;
            let gameTagInfo = (generalSettings && generalSettings.gamesSettings && generalSettings.gamesSettings.gamesTags && generalSettings.gamesSettings.gamesTags[gameType]) || {};

            // for now, rawGameData will contain also gameTagInfo properties - which are read by adminClient settings
            // moreover, we'll add a new property on it regarding allowSwitchToDemo
            let rawGameData = Object.assign(sdkInfo, gameTagInfo);
            let gameInfo = new GameInfo(rawGameData, this.services);

            gamesMap[gameInfo.gameType] = gameInfo;
            
        }
      } catch(err) {
        this.services.logger.error(`Failed to generate GameInfo for game type ${gameType}`, err);
      }

    });

    return gamesMap;
  }


  private _updateGamesMapPromise: Promise<void> = Promise.resolve();

  getGamesMapUpdatePromise(): Promise<void> {
    return this._updateGamesMapPromise;
  }

  private async _updateGamesMap(): Promise<void> {
    let usedGameTypesBySite = this.services.configuration.usedGameTypesBySite || [];
    if(usedGameTypesBySite.length === 0) {
      this.services.logger.error(`configuration.usedGameTypesBySite returned empty array`);
      return;
    }

    const gamesMap = await this._getGamesInfoMap(usedGameTypesBySite);

    this._setGamesMap(gamesMap);
  }

  async _getGamesInfoMap(gameTypes: number[]): Promise<GamesMap> {
    try {
      let sdkGamesInfo = await this.services.sdk.getGamesInfo(gameTypes);
      
      return await this._buildGamesMap(sdkGamesInfo);
    } catch (err) {
      this.services.logger.error(`sdk.getGamesInfo failed in GamesService.getGamesInfo`, err);
      return {};
    }
  }

   async getUnrestrictedGamesInfoMap(gameTypes: number[]): Promise<GamesMap> {
    if (!gameTypes || gameTypes.length === 0) {
        return {};
    }


    if (Object.keys(this._gamesMap).length > 0) {
      // fill data from cache if there is something in the cache
        const inCacheGames = gameTypes.filter((gt) => this.gamesMap[gt]);
        return inCacheGames.reduce((acc, gt) => {acc[gt] = this.gamesMap[gt]; return acc;}, {});
    } else {
        // otherwise fill it from SDK
        const gamesMap = await this._getGamesInfoMap(gameTypes);
        const result: GamesMap = {};
        for(let gameInfo of Object.values(gamesMap)) {
          if(gameInfo.restriction.isGameAllowedInTheGrid()) {
            result[gameInfo.gameType] = gameInfo;
          }
        }
        return result;
    }
  }

  async getGamesInfoForHistory(gameTypes: number[]): Promise<GamesMap> {
    let sdkGamesInfo = await this.services.sdk.getGamesInfoForHistory(gameTypes);

    let gamesMap: GamesMap = {};
      Object.keys(sdkGamesInfo).forEach(gameType => {
          gamesMap[gameType] = new GameInfo(sdkGamesInfo[gameType], this.services)
      });
      return gamesMap;
  }

  async getUnrestrictedGamesInfoArray(gameTypes: number[]): Promise<GameInfo[]> {
    let gamesMap = await this.getUnrestrictedGamesInfoMap(gameTypes);

    const result: GameInfo[] = [];
    for(let i = 0; i < gameTypes.length; i++) {
      const gameType = gameTypes[i];
      if (gamesMap[gameType]) {
        result.push(gamesMap[gameType]);
      }
    }
    return result;
  }

  async getUnrestrictedGameInfo(gameType: number): Promise<GameInfo> {
    const gamesMap = await this.getUnrestrictedGamesInfoMap([gameType]);
    if(gamesMap) {
      return gamesMap[gameType];
    } else {
      return null;
    }
  }

  async getCorrelatedGames(gameType: number, numberOfGames: number): Promise<number[]> {
    return await this._correlatedGamesReader.getCorrelatedGame(gameType, numberOfGames);
  }

  getGameImageExpectedSizes(useSquareIcons?: boolean, animated?: boolean): IGameImageSize {
    if (useSquareIcons) {
      if (animated) {
        return {
          width: 210,
          height: 292
        };
      }
      return {
        width: 400,
        height: 555
      };
    }

    return {
      width: 210,
      height: 210
    };
  }

  saveGame(gameType: number): void {
    if (this.savedGameTypes.indexOf(gameType) >= 0) {
      return;
    }

    let newSavedGames: number[] = [gameType, ...this.savedGameTypes].slice(0, this._MAX_SAVED_GAMES);
    this.services.localStorage.setJson(SAVED_GAMES_LOCAL_STORAGE_KEY, newSavedGames);
    this._setInMemorySavedGames(newSavedGames)
  }

  removeSavedGame(gameType: number): void {
    let newSavedGames: number[] = this.savedGameTypes.filter(gt => gt !== gameType);
    this.services.localStorage.setJson(SAVED_GAMES_LOCAL_STORAGE_KEY, newSavedGames);
    this._setInMemorySavedGames(newSavedGames)
  }

  private _MAX_SAVED_GAMES: number = 10; // Max number of games that can be saved at a given time.
  private _DAILY_INCREMENT_SAVED_COUNTER: number = 2; // The fake counter (per gameType) will increase by this value per day passed since _START_DATE
  private _INFERIOR_THRESHOLD_BASE_COUNTER: number = 100;
  private _SUPERIOR_THRESHOLD_BASE_COUNTER: number = 1000;
  private _START_DATE: Date = new Date('April 12, 2024 00:00:00');

  //We generate a number in the _INFERIOR_THRESHOLD_BASE_COUNTER - _SUPERIOR_THRESHOLD_BASE_COUNTER interval based on the gametype.
  // Then, to that number we add _DAILY_INCREMENT_SAVED_COUNTER * (days passed since _START_DATE)
  public generateTimesSavedCounterForGame(gameType: number): number {
    let hash = this.services.tools.getHash(gameType.toString());

    let daysSinceStart = Math.round((new Date().getTime() - this._START_DATE.getTime())/ (1000 * 3600 * 24));

    let baseHash = Math.abs(hash) % (this._SUPERIOR_THRESHOLD_BASE_COUNTER - this._INFERIOR_THRESHOLD_BASE_COUNTER) + this._INFERIOR_THRESHOLD_BASE_COUNTER;

    return baseHash + (daysSinceStart * this._DAILY_INCREMENT_SAVED_COUNTER);
  }

  public async showAllSavedGamesDrawer(): Promise<void> {
    const savedGamesSwiperConfig: SwiperGamesListConfiguration = {
      gamesId: this.savedGameTypes,
      display: true,
      title: this.services.translation.translate("MY_SAVED_GAMES"),
      useSquareIcons: true
    };

    await (await this.services.verticalGamesList).renderList(savedGamesSwiperConfig);
  }
}
